import Breadcrumb from "@/components/Breadcrumb";
import {
  ArrowRight,
  Globe,
  Hotel,
  Smartphone,
  Megaphone,
  Bot,
  Lightbulb,
  ShieldCheck,
  Target,
  Cpu,
  Settings,
} from "lucide-react";
import Link from "next/link";
import { Helmet } from "react-helmet-async";
import { SITE_URL, normalizeLocale, sitePageUrl, ogLocaleTag } from "@/lib/site";

export default function ServicesPage({ params }) {
  const locale = normalizeLocale(params?.locale);
  const pageUrl = sitePageUrl(locale, "services");
  const homeUrl = sitePageUrl(locale);
  const orgId = `${SITE_URL}/#organization`;

  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "Services" },
  ];

  const services = [
    {
      slug: "ai-apps-solutions",
      icon: Bot,
      title: "AI Apps & Solutions",
      body: "As an experienced AI solutions company, we design and develop smart AI applications, chatbots, assistants, and custom tools tailored to your business needs. These solutions improve customer experience, automate repetitive tasks, and unlock new growth opportunities.",
      bgImage: "/assets/img-17.jpeg",
    },
    {
      slug: "business-automation-ai",
      icon: Globe,
      title: "Business Automation & AI",
      body: "Our AI automation services help businesses streamline operations, reduce manual work, and improve efficiency. Using AI for business automation, we build intelligent workflows that save time, reduce errors, and allow teams to focus on higher-value work.",
      bgImage: "/assets/img-20.jpg",
    },
    {
      slug: "website-design-development",
      icon: Hotel,
      title: "Website Design & Development",
      body: "As a modern web development company in Switzerland, we create fast, responsive, and conversion-focused websites. Our websites are built to strengthen your brand, improve user experience, and generate more qualified leads.",
      bgImage: "/assets/img-40.jpg",
    },
    {
      slug: "ecommerce-development",
      icon: Megaphone,
      title: "E-commerce Development",
      body: "We build scalable online stores as an eCommerce development company in Switzerland, focusing on better shopping experiences, smooth checkout flows, and improved sales performance for growing businesses.",
      bgImage: "/assets/img-43.jpg",
    },
    {
      slug: "hotel-digital-solutions",
      icon: Lightbulb,
      title: "Hotel Digital Solutions",
      body: "Our hotel digital solutions are designed for hospitality businesses that want to increase direct bookings and improve guest experience. We provide booking-focused platforms, modern websites, and hotel IT solutions in Switzerland that help reduce reliance on third-party channels.",
      bgImage: "/assets/img-37.jpeg",
    },
    {
      slug: "saas-startup-products",
      icon: Smartphone,
      title: "SaaS & Startup Products",
      body: "As a trusted SaaS development company, we help startups and businesses build scalable digital products, MVPs, and platforms designed for long-term growth and performance across European markets.",
      bgImage: "/assets/img-42.jpg",
    },
    {
      slug: "marketing-tech-consulting",
      icon: ShieldCheck,
      title: "Marketing & Tech Consulting",
      body: "Our AI consulting services in Europe help businesses align their marketing, digital systems, and technology strategies. We guide you in making smarter decisions that support growth, efficiency, and long-term success.",
      bgImage: "/assets/img-38.jpg",
    },
  ];

  const whyChooseUs = [
    {
      icon: Target,
      title: "Business-Focused Solutions",
      description:
        "Every service is designed to help businesses generate more leads, improve bookings, and increase operational efficiency through AI automation services and smart digital systems.",
    },
    {
      icon: Cpu,
      title: "Industry Understanding",
      description:
        "We work closely with hospitality, tourism, and service-based businesses, giving us strong expertise in hotel digital solutions and real-world operational challenges.",
    },
    {
      icon: Settings,
      title: "Modern Technology",
      description:
        "We use reliable, scalable, and up-to-date digital tools to build systems that perform well and support long-term growth.",
    },
    {
      icon: ShieldCheck,
      title: "Tailored Approach",
      description:
        "No generic templates. Every solution is customized based on your business goals, audience, and growth strategy — whether it’s AI for business automation, SaaS, or website development.",
    },
    {
      icon: ArrowRight,
      title: "Long-Term Support",
      description:
        "We don’t just launch and leave. We continue optimizing and improving your systems to ensure consistent performance, better results, and ongoing growth.",
    },
  ];

  const servicesPageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": orgId,
        name: "Highlandgroup",
        url: `${SITE_URL}/`,
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/logo.png`,
        },
      },
      {
        "@type": "CollectionPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "AI Automation Services & Software Development",
        description:
          "Explore AI automation services, websites, eCommerce, SaaS, hotel digital solutions, and tech consulting for Swiss and European businesses.",
        inLanguage: locale,
        about: { "@id": orgId },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
          { "@type": "ListItem", position: 2, name: "Services", item: pageUrl },
        ],
      },
      {
        "@type": "ItemList",
        "@id": `${pageUrl}#itemlist`,
        name: "Highlandgroup Services",
        itemListElement: services.map((service, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: sitePageUrl(locale, "services", service.slug),
          name: service.title,
          description: service.body,
        })),
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>AI Automation Services & Software Development</title>

        <meta
          name="description"
          content="Explore AI automation services, websites, eCommerce, SaaS, hotel digital solutions, and tech consulting for Swiss and European businesses."
        />
        <meta
          name="keywords"
          content="AI automation services, software development services, business automation services Europe"
        />
        <meta name="robots" content="index, follow" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:title" content="Highlandgroup Services" />
        <meta
          property="og:description"
          content="AI automation, websites, SaaS, hotel digital solutions, and consulting for business growth."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:locale" content={ogLocaleTag(locale)} />
        <meta property="og:site_name" content="Highlandgroup" />
        <meta property="og:image" content={`${SITE_URL}/logo.png`} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Highlandgroup Services" />
        <meta
          name="twitter:description"
          content="AI automation, websites, SaaS, hotel digital solutions, and consulting for business growth."
        />
        <meta name="twitter:image" content={`${SITE_URL}/logo.png`} />

        <script type="application/ld+json">
          {JSON.stringify(servicesPageSchema)}
        </script>
      </Helmet>

      <main className="min-h-screen bg-bg-soft text-charcoal selection:bg-accent-red/20 selection:text-charcoal">
        {/* Hero */}
        <Breadcrumb
          items={breadcrumbItems}
          title="AI Automation Services & Digital Solutions for Business Growth"
          subtitle="Highland Group provides AI automation services, custom software, and digital solutions for businesses that want stronger visibility, better customer experience, and measurable growth. As an AI solutions company and software development company in Switzerland, we help businesses across Switzerland and Europe build smarter systems that improve performance and scale efficiently."
          description="From websites and SaaS platforms to AI for business automation and hotel digital solutions, our services are designed to deliver real business results — not just technical outputs."
        />

        {/* Services Section */}
        <section className="py-16  border-b border-border-light bg-bg-soft">
          <div className="container mx-auto px-5 sm:px-8 md:px-12 lg:px-16">
            <div className="mb-12">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-charcoal">
                What We Do Best
              </h2>
              <p className="mt-6 max-w-3xl text-lg text-muted">
                We combine strategy, technology, and execution to deliver systems that are built to perform, scale, and support long-term business growth.
              </p>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {services.map((service) => {
                const Icon = service.icon;

                return (
                  <Link
                    key={service.slug}
                    href={`/${locale}/services/${service.slug}`}
                    className="h-full"
                  >
                    <article className="group relative h-full flex flex-col rounded-2xl border border-border-light overflow-hidden hover:-translate-y-2 hover:shadow-xl transition-all">
                      <div
                        className="absolute inset-0 bg-cover bg-center opacity-10 group-hover:opacity-20"
                        style={{ backgroundImage: `url(${service.bgImage})` }}
                      />

                      <div className="relative p-6 flex flex-col flex-1">
                        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-accent-red/10 text-accent-red">
                          <Icon className="w-7 h-7" />
                        </div>

                        <h3 className="text-xl font-bold mb-3 group-hover:text-accent-red">
                          {service.title}
                        </h3>

                        <p className="text-muted text-sm leading-relaxed flex-1">
                          {service.body}
                        </p>
                      </div>
                    </article>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 ">
          <div className="container mx-auto px-5 sm:px-8 md:px-12 lg:px-16">
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold">
                Why Choose Us?
              </h2>
              <p className="mt-4 text-lg text-muted max-w-2xl">
                Highland Group is not just a service provider — we are a digital growth partner focused on real business outcomes.
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {whyChooseUs.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    className="p-6 border rounded-2xl hover:shadow-lg transition-all"
                  >
                    <div className="mb-4 text-accent-red">
                      <Icon className="w-7 h-7" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                    <p className="text-muted text-sm">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-bg-soft border-t border-border-light">
          <div className="container mx-auto px-5 sm:px-8 md:px-12 lg:px-16 text-center max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal">
              Build Smarter Digital Systems for Your Business
            </h2>
            <p className="mt-4 text-lg text-muted">
              We help businesses grow with AI automation services, custom development, and digital solutions designed for real results.
            </p>
            <Link
              href="/contact"
              className="mt-6 inline-block rounded-full bg-accent-red px-8 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              Book your free consultation today
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}